import { useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import { useGetDrumBallCertificateListDataQuery } from "../../../../store/queries/DrumBall";
import {
  updateConfig,
  getDrumBallListData,
} from "../../../../store/slices/DrumBall/drumBallSlice";
import { updateConfig as globalUpdateConfig } from "../../../../store/slices/Global";
import { saveDrumBall, passAll } from "../api";
import { toast } from "react-toastify";
import { debounce } from "../../../../utils/hooks/useDebounce";

const useDrumBallCertificate = () => {
  const dispatch = useDispatch();
  const drumBallState = useSelector((state) => state.drumBall);
  const { passAllModal } = useSelector((state) => state.global);
  const slug = sessionStorage?.getItem("slug");

  const formik = useFormik({
    initialValues: {
      remark: "",
    },
  });
  const [searchInput, setSearchInput] = useState(
    drumBallState?.certificate?.search || "",
  );

  const paginationOptions = [
    { label: "20 Per page", value: 20 },
    { label: "40 Per page", value: 40 },
    { label: "60 Per page", value: 60 },
  ];

  const queryArgs = {
    page_size: drumBallState.certificate?.currentPageSize,
    page: drumBallState.certificate?.currentPage,
    sort_by: drumBallState.sortBy,
    sort_order: drumBallState.sortOrder,
    process_slug: slug,
    receipt_id: drumBallState.selectedCardId,
    search: drumBallState.certificate?.search,
  };

  const {
    data: certificateList,
    refetch,
    isFetching,
    isLoading,
  } = useGetDrumBallCertificateListDataQuery(queryArgs, {
    skip: !drumBallState.selectedCardId,
  });

  const disableMultiSelect = useMemo(
    () => certificateList?.data?.data?.every((item) => item.disable === true),
    [certificateList],
  );

  const tableFields = useMemo(() => {
    const backendFields = certificateList?.data?.fields;
    if (backendFields && Object.keys(backendFields).length > 0) {
      return backendFields;
    }
    return {
      certificate_number: { label: "Certificate Number", sortable: false },
      item: { label: "Item", sortable: false },
      status: { label: "Status", sortable: false },
    };
  }, [certificateList]);

  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.certificate.currentPageSize = page_size;
        state.certificate.currentPage = 1;
      }),
    );
  };

  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.certificate.currentPage = page;
      }),
    );
  };

  const debouncedCertificateSearch = useMemo(
    () =>
      debounce((searchValue) => {
        dispatch(
          updateConfig((state) => {
            state.certificate.search = searchValue;
            state.certificate.currentPage = 1;
          }),
        );
      }, 500),
    [dispatch],
  );

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedCertificateSearch(value);
  };

  const handleClearClick = () => {
    setSearchInput("");
    dispatch(updateConfig((state) => (state.certificate.search = "")));
  };

  const getRow = (field, data) => {
    const rows = {
      common: (field, data) => <p className="pro-mb-0">{data[field]}</p>,
      status: (_, data) =>
        data?.status === "Rejected" ? (
          <span className={"pro-badge badge-danger-outline pro-d-inline-block"}>
            {"Rejected"}
          </span>
        ) : data?.status === "Marked" ? (
          <span className={"outline-primary pro-badge pro-d-inline-block"}>
            {"Marked"}
          </span>
        ) : data?.status === "Completed" ? (
          <span className={"outline-success pro-badge pro-d-inline-block"}>
            {"Completed"}
          </span>
        ) : data?.status === "Pending" ? (
          <span className={"outline-warning pro-badge pro-d-inline-block"}>
            {"Pending"}
          </span>
        ) : (
          <span className={"outline pro-badge pro-d-inline-block"}>
            {data?.status}
          </span>
        ),
    };
    return rows?.[field]?.(field, data) ?? rows["common"](field, data);
  };

  const handlePassAllClick = () => {
    dispatch(
      globalUpdateConfig((state) => (state.passAllModal = !state.passAllModal)),
    );
  };

  const handlePassAll = () => {
    const data = {
      stock_receipt_id: drumBallState?.selectedCardId,
      process_slug: slug,
      remarks: formik?.values?.remark,
    };

    passAll(data).then((response) => {
      if (response?.data?.status_code === 200) {
        toast.success(response?.data?.message);
        refetch();
        handlePassAllClick();
        formik?.resetForm();
        dispatch(updateConfig((state) => (state.clear = true)));
        const timeOut = setTimeout(() => {
          dispatch(updateConfig((state) => (state.clear = false)));
          clearTimeout(timeOut);
        }, 1000);
        dispatch(
          updateConfig((state) => {
            state.drumBallData.Pending = [];
          }),
        );
        dispatch(
          getDrumBallListData({
            page: 1,
            per_page: drumBallState?.certificate?.currentPageSize,
            process_slug: slug,
          }),
        );
      } else if (response?.status_code === 403) {
        toast.error(response?.message ?? "Error ");
      } else {
        toast.error(response?.data?.message);
      }
    });
  };

  const handleConfirmModal = (selectedIds) => {
    dispatch(
      updateConfig((state) => {
        state.showAddDrumBallModal = !state.showAddDrumBallModal;
        state.selectedDrumBall = selectedIds;
      }),
    );
  };

  const handleConfirm = (values) => {
    const body = {
      item_details_id: drumBallState.selectedDrumBall,
      status: values?.status ?? "",
      remark: values?.remarks ?? "",
    };
    const payload = Object.entries(body)
      .filter(([, v]) => v !== undefined && v !== "" && v !== null)
      .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});

    saveDrumBall(payload).then((response) => {
      if (response?.data?.status_code === 200) {
        toast.success(response?.data?.message ?? "Saved successfully");
        dispatch(
          updateConfig((state) => {
            state.showAddDrumBallModal = false;
            state.selectedDrumBall = "";
            state.clear = true;
          }),
        );
        refetch();
        const timeOut = setTimeout(() => {
          dispatch(updateConfig((state) => (state.clear = false)));
          clearTimeout(timeOut);
        }, 1000);
      } else {
        toast.error(response?.data?.message ?? "Something went wrong");
        dispatch(updateConfig((state) => (state.showAddDrumBallModal = false)));
      }
    });
  };

  return {
    paginationOptions,
    disableMultiSelect,
    drumBallState,
    certificateList,
    tableFields,
    isFetching,
    isLoading,
    searchInput,
    formik,
    passAllModal,
    handleSearch,
    handleClearClick,
    handlePageSize,
    handlePagination,
    getRow,
    handleConfirmModal,
    handleConfirm,
    handlePassAllClick,
    handlePassAll,
  };
};

export default useDrumBallCertificate;
